/**
 * Opening or closing for each session of one contract.
 *
 * The change in open interest answers this: a rise means contracts were created, a fall
 * means they were retired. Volume alone cannot — the same 200 contracts opened and shut
 * twice trade 400.
 *
 * **Which session a change belongs to.** Open interest is stamped once, around 06:30 ET,
 * before the session opens, so the figure dated D reports positions as they stood after
 * D−1 finished trading. The change between the rows dated D−1 and D therefore describes
 * D−1's flow, not D's. That is not a guess: across four contracts and 143 sessions,
 * attributing it to D made open interest move further than that day's volume 35 times —
 * arithmetically impossible — while attributing it to D−1 did so 6 times, all of which are
 * explained by exercise and assignment, which retire contracts with no trade at all.
 *
 * **The consequence for today.** Today's own session has no confirming print yet: its
 * open-interest figure arrives next morning. It is reported as pending rather than labelled,
 * because the honest answer to "did today's flow open or close" is that open interest has
 * not been published yet.
 *
 * A COMPLETED session is a different case, and treating it like today was a bug. Once the
 * next morning has happened its figure exists — OPRA stamps it around 06:30 ET — but it does
 * not join the EOD series until that following session closes and earns a row of its own. So
 * a rule of "confirmed by the next ROW" left yesterday pending for a full extra day, with the
 * confirming number already sitting in the live snapshot. Pass it as `publishedOi` and the
 * last completed session is settled the morning after it traded, as it should be. Two things can still be said in the meantime, and both are here:
 * volume above open interest proves at least the excess had to be newly created, and a
 * prior session that opened a comparable position is a candidate for what today's flow may
 * be closing.
 *
 * It costs nothing. The EOD response already carries `open_interest` and `volume` on every
 * row, so this is arithmetic over data the contract view has fetched anyway.
 */

import type { FlowPositionLabel } from './flowVerdictLabels';

export interface EodPositionEffectOptions {
  /**
   * Open interest published for the most recent COMPLETED session, from the live snapshot.
   * It reflects that session's close, not today's trading, because OPRA stamps it before
   * today opened.
   */
  publishedOi?: number | null;
  /** Today's date (ET, `YYYY-MM-DD`). The published figure only confirms a session before it. */
  today?: string | null;
}

export interface EodOiRow {
  /** The API returns `tradetime`; the modal's normalised rows carry `date`. Either works. */
  tradetime?: string | null;
  date?: string | null;
  open_interest?: number | null;
  volume?: number | null;
}

export interface EodPositionEffect {
  /** Null while pending, or when there is no next session to confirm against. */
  label: FlowPositionLabel;
  /** Net contracts created (+) or retired (−) by this session's trading. */
  change: number | null;
  /** Open interest as reported the following morning, which is what confirms this session. */
  confirmedBy: string | null;
  /**
   * |change| as a share of the session's volume, 0–1.
   *
   * Near 1 means the day's trading went almost entirely into new or closed positions. Near 0
   * means it was opened and shut the same day — real volume that left no position behind,
   * which is worth distinguishing from a quiet day.
   */
  conviction: number | null;
  /** True for the latest session: its confirming open-interest figure is not published yet. */
  pending: boolean;
  /**
   * Contracts that must be new, from arithmetic alone: volume above open interest.
   *
   * Only `open_interest` contracts exist to be closed, so any excess had to be created. This
   * is available immediately and does not wait for the next morning's print.
   */
  provableNew: number | null;
}

export const NO_POSITION_EFFECT: EodPositionEffect = {
  label: null,
  change: null,
  confirmedBy: null,
  conviction: null,
  pending: false,
  provableNew: null,
};

function dayOf(row: EodOiRow | undefined): string {
  const raw = row?.tradetime ?? row?.date;
  return typeof raw === 'string' ? raw.trim() : '';
}

function oiOf(row: EodOiRow | undefined): number | null {
  if (!row) return null;
  const n = Number(row.open_interest);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function volOf(row: EodOiRow | undefined): number {
  const n = Number(row?.volume);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Effect for every session, keyed by its own date.
 *
 * Rows are sorted here rather than trusted in arrival order: the contract view fetches EOD in
 * pages, in parallel after the first, so a change taken against the wrong neighbour would
 * invert the answer.
 */
export function eodPositionEffects(
  rows: EodOiRow[],
  options: EodPositionEffectOptions = {},
): Map<string, EodPositionEffect> {
  const out = new Map<string, EodPositionEffect>();
  if (!Array.isArray(rows) || rows.length === 0) return out;

  const dated = rows
    .filter((r) => r && dayOf(r))
    .slice()
    .sort((a, b) => dayOf(a).localeCompare(dayOf(b)));

  for (let i = 0; i < dated.length; i++) {
    const day = dayOf(dated[i]);
    const todayOi = oiOf(dated[i]);
    const volume = volOf(dated[i]);
    // Volume above open interest is provable now: only `open_interest` contracts exist to
    // be closed, so the excess had to be created.
    const provableNew = todayOi != null && volume > todayOi ? volume - todayOi : null;

    // This session is confirmed by the *next* row's open interest, not its own.
    const nextRow = i + 1 < dated.length ? dated[i + 1] : undefined;
    let nextOi = oiOf(nextRow);
    let confirmedBy: string | null = nextRow ? dayOf(nextRow) : null;

    // The last row has no following row, but a COMPLETED session still has a published
    // figure: it is stamped the next morning and sits in the live snapshot long before the
    // following session closes and earns a row. Using it is what stops yesterday reading
    // "Pending" all day today.
    const published = Number(options.publishedOi);
    if (nextRow === undefined && Number.isFinite(published) && published > 0 && options.today) {
      if (day && day < String(options.today)) {
        nextOi = published;
        confirmedBy = String(options.today);
      }
    }

    // Pending means one specific thing: this is the newest session and its figure has not
    // been published yet. A row in the MIDDLE of the range whose neighbour reports no open
    // interest is not pending — nothing further is coming for it — it simply cannot be
    // answered. Conflating the two put "Pending" on the oldest row in the table.
    if (nextRow === undefined && nextOi == null) {
      out.set(day, { ...NO_POSITION_EFFECT, pending: true, provableNew });
      continue;
    }
    if (nextOi == null || todayOi == null) {
      out.set(day, { ...NO_POSITION_EFFECT, provableNew });
      continue;
    }

    const change = nextOi - todayOi;
    out.set(day, {
      label: change > 0 ? 'Opening' : change < 0 ? 'Closing' : 'Flat',
      change,
      confirmedBy,
      conviction: volume > 0 ? Math.min(1, Math.abs(change) / volume) : null,
      pending: false,
      provableNew,
    });
  }

  return out;
}

/** A past session whose opening this flow could plausibly be unwinding. */
export interface CounterFlowCandidate {
  date: string;
  opened: number;
  /** How closely its size matches, 0–1. */
  match: number;
}

/**
 * Earlier sessions that opened a position of comparable size to `volume`.
 *
 * Offered as candidates, never as a conclusion. Matching sizes is suggestive and no more:
 * two unrelated traders can print the same quantity, and a position can be unwound in pieces
 * across several days. It is shown because when today's flow is 5,000 contracts and one
 * session three weeks ago opened 5,000, that coincidence is worth a look — and because
 * until tomorrow's open-interest print there is nothing stronger available.
 */
export function counterFlowCandidates(
  rows: EodOiRow[],
  effects: Map<string, EodPositionEffect>,
  volume: number,
  { tolerance = 0.25, limit = 3 }: { tolerance?: number; limit?: number } = {},
): CounterFlowCandidate[] {
  if (!Array.isArray(rows) || !(volume > 0)) return [];

  const out: CounterFlowCandidate[] = [];
  for (const row of rows) {
    const day = dayOf(row);
    const e = effects.get(day);
    if (!e || e.label !== 'Opening' || e.change == null || e.change <= 0) continue;
    const diff = Math.abs(e.change - volume) / volume;
    if (diff <= tolerance) out.push({ date: day, opened: e.change, match: 1 - diff });
  }

  out.sort((a, b) => b.match - a.match || b.date.localeCompare(a.date));
  return out.slice(0, limit);
}

/** Compact cell text, e.g. `Opening +1,775`. */
export function formatPositionEffect(e: EodPositionEffect | undefined): string {
  if (!e) return '—';
  if (e.pending) return 'Pending';
  if (e.label == null) return '—';
  if (e.change == null || e.change === 0) return e.label;
  return `${e.label} ${e.change > 0 ? '+' : ''}${e.change.toLocaleString()}`;
}
